<template>
  <div class="voice-input-icon" @click="startVoiceInput" :class="{ 'recording': isRecording, 'loading': isProcessing || isInitializing }">
    <!-- AI处理中或初始化中的loading图标 -->
    <div v-if="isProcessing || isInitializing" class="loading-animation">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="loading-spinner">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="31.4 31.4" fill="none"/>
      </svg>
    </div>
    
    <!-- 默认状态的麦克风图标 -->
    <svg v-else-if="!isRecording" width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="9" y="4" width="6" height="10" rx="3" stroke="currentColor" stroke-width="2" fill="none"/>
      <path d="M6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <line x1="12" y1="18" x2="12" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>
    
    <!-- 录音中的动画 -->
    <div v-else class="recording-animation">
      <div class="pulse-ring"></div>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="9" y="4" width="6" height="10" rx="3" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <line x1="12" y1="18" x2="12" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </div>
  </div>
</template>

<script>
import SpeechRecognizer from '@/utils/speech/speechrecognizer.js';
import WebRecorder from '@/utils/speech/webrecorder.js';
import { mapGetters } from 'vuex';
import store from '@/store';

export default {
  name: 'VoiceInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    // 新增：组件类型（Input/Picker）
    componentType: {
      type: String,
      default: 'Input'
    },
    // 新增：字段名称
    fieldName: {
      type: String,
      default: ''
    },
    // 新增：字段标题
    fieldTitle: {
      type: String,
      default: ''
    },
    // 新增：AI处理中状态
    isProcessing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isRecording: false,
      isInitializing: false, // 新增：初始化中状态（点击后到识别开始前）
      recorder: null,
      speechRecognizer: null,
      isCanSendData: false,
      isCanStop: false,
      recognitionText: '',
      resultText: '',
      microphonePermissionGranted: false,
      autoStopTimer: null, // 自动停止计时器
      AUTO_STOP_DELAY_EMPTY: 5000, // 无输入时3秒自动停止
      AUTO_STOP_DELAY_HAS_INPUT: 1400, // 有输入时1.5秒自动停止
      sessionId: '', // 会话ID，用于追踪单次语音输入的完整流程
      startTime: null, // 开始时间
      
      // 语音识别配置
      speechConfig: {
        signCallback: this.signCallback,
        appid: '',
        hotword_id: "556aab3bad8e11f0b984446a2e8b4d02",
        secretid: '',
        secretKey: '',
        region: 'ap-guangzhou',
        engineType: '16k_zh_large',
        voice_format: 1,
        needvad: 1,
        filter_dirty: 2,
        filter_modal: 2,
        filter_punc: 1,
        convert_num_mode: 1,
        word_info: 2
      }
    };
  },
  computed: {
    ...mapGetters(['getConfigItem'])
  },
  mounted() {
    this.initSpeechConfig();
  },
  beforeDestroy() {
    this.clearAutoStopTimer();
    this.cleanup();
  },
  methods: {
    // 生成会话ID
    generateSessionId() {
      return `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
    
    // Aegis 日志上报
    reportToAegis(data) {
      try {
        const aegis = store.state.user?.aegis;
        if (!aegis) {
          console.warn('[VoiceInput] Aegis 未初始化');
          return;
        }
        
        // 添加通用信息
        const reportData = {
          ...data,
          sessionId: this.sessionId,
          componentType: this.componentType,
          fieldName: this.fieldName,
          fieldTitle: this.fieldTitle,
          userid: sessionStorage.getItem('userid') || '',
          timestamp: new Date().toISOString(),
        };
        
        aegis.infoAll({
          msg: JSON.stringify(reportData),
          ext1: data.ext1 || 'voice-input',
          trace: data.trace || 'voice-input',
        });
        
        console.log('[VoiceInput] Aegis 上报:', reportData);
      } catch (error) {
        console.error('[VoiceInput] Aegis 上报失败:', error);
      }
    },
    
    // 初始化语音配置
    initSpeechConfig() {
       try {
        const config = JSON.parse(this.getConfigItem('voiceConfig'));
        this.speechConfig.appid = config?.app;
        this.speechConfig.secretKey = atob((config.id || '').slice(1));
        this.speechConfig.secretid = atob((config.key || '').slice(0, -1));
        const remoteConfig = JSON.parse(this.getConfigItem('remoteConfig') || '{}');
        this.speechConfig = { ...this.speechConfig, ...(remoteConfig || {})}
      } catch (e) {
      }
      
    },

    toUint8Array(wordArray) {
      const words = wordArray.words;
      const sigBytes = wordArray.sigBytes;
      const u8 = new Uint8Array(sigBytes);
      for (let i = 0; i < sigBytes; i++) {
        u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
      }
      return u8;
    },

    Uint8ArrayToString(fileData) {
      let dataString = '';
      for (let i = 0; i < fileData.length; i++) {
        dataString += String.fromCharCode(fileData[i]);
      }
      return dataString;
    },
    
    // 签名回调函数
     signCallback(signStr) {
        const secretKey = this.speechConfig.secretKey;
      const hash = window.CryptoJSTest.HmacSHA1(signStr, secretKey);
      const bytes = this.Uint8ArrayToString(this.toUint8Array(hash));
      console.log(window.btoa(bytes))
      return window.btoa(bytes);
    },
    
    async requestMicrophonePermission() {
      try {
        console.log('[VoiceAssistant] Requesting microphone permission...');

        // 检查浏览器是否支持 getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('浏览器不支持录音功能');
        }

        // 申请麦克风权限
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        console.log('[VoiceAssistant] Microphone permission granted');

        // 立即停止流，我们只是为了获取权限
        stream.getTracks().forEach(track => track.stop());

        // 更新权限状态
        this.microphonePermissionGranted = true;
        return true
      } catch (error) {
        console.error('[VoiceAssistant] Failed to get microphone permission:', error);

        let errorMessage = '无法获取录音权限';
        let errorType = 'unknown';
        
        if (error.name === 'NotAllowedError') {
          errorMessage = '麦克风权限被拒绝，请在手机系统设置中允许麦克风权限';
          errorType = 'permission_denied';
        } else if (error.name === 'NotFoundError') {
          errorMessage = '未找到麦克风设备';
          errorType = 'device_not_found';
        } else if (error.name === 'NotSupportedError') {
          errorMessage = '浏览器不支持录音功能';
          errorType = 'not_supported';
        }

        this.microphonePermissionGranted = false;

        // 上报：麦克风权限获取失败
        this.reportToAegis({
          type: 'voice_input_microphone_permission_error',
          errorType: errorType,
          errorName: error.name || 'unknown',
          errorMessage: errorMessage,
          error: error.message || error.toString(),
          browser: navigator.userAgent,
          ext1: 'voice-input-error',
          trace: 'voice-input-permission-error',
        });

        // 显示错误提示
        this.$toast({
          message: errorMessage,
          type: 'fail',
          duration: 3000
        });
        return false
      }
    },
    
    // 开始语音输入
    async startVoiceInput(e) {
        e.stopPropagation(); 
        this.$emit('startVoiceInput');
        
      // 如果正在处理中，禁止点击
      if (this.isProcessing || this.isInitializing) {
        return;
      }
        
      if (this.isRecording) {
        this.stopVoiceInput();
        return;
      }
        this.$toast('请稍等');
      
      // 设置初始化状态，显示 loading 图标
      this.isInitializing = true;
      
      // 生成新的会话ID
      this.sessionId = this.generateSessionId();
      this.startTime = Date.now();
      
      // 上报：用户点击开始语音输入
      this.reportToAegis({
        type: 'voice_input_click',
        action: 'start',
        initialValue: this.value || '',
        ext1: 'voice-input-action',
        trace: 'voice-input-click',
      });
      
      // 检查配置
      if (!this.speechConfig.appid || !this.speechConfig.secretid || !this.speechConfig.secretKey) {
        this.isInitializing = false; // 重置初始化状态
        this.$toast({
          message: '语音识别配置未完成',
          type: 'fail'
        });
        
        // 上报：配置错误
        this.reportToAegis({
          type: 'voice_input_config_error',
          error: '语音识别配置未完成',
          ext1: 'voice-input-error',
          trace: 'voice-input-config-error',
        });
        
        return;
      }
      
      // 请求麦克风权限
      if (!this.microphonePermissionGranted) {
        const granted = await this.requestMicrophonePermission();
        if (!granted) {
          this.isInitializing = false; // 重置初始化状态
          // 上报：权限被拒绝
          this.reportToAegis({
            type: 'voice_input_permission_denied',
            error: '麦克风权限被拒绝',
            ext1: 'voice-input-error',
            trace: 'voice-input-permission-denied',
          });
          return;
        }
      }
      
      try {
        this.$emit('start')

        this.isRecording = true;
        this.recognitionText = '';
        this.resultText = '';
        
        // 初始化录音器
        this.recorder = new WebRecorder();
        
        // 设置录音数据回调
        this.recorder.OnReceivedData = (res) => {
          if (this.isCanSendData && this.speechRecognizer) {
            this.speechRecognizer.write(res);
          }
        };
        
        // 录音失败回调
        this.recorder.OnError = (err) => {
          console.error('[VoiceInput] 录音失败:', err);
          
          this.isInitializing = false; // 重置初始化状态
          
          // 上报：录音失败
          this.reportToAegis({
            type: 'voice_input_recorder_error',
            error: err?.message || JSON.stringify(err),
            ext1: 'voice-input-error',
            trace: 'voice-input-recorder-error',
          });
          
          // this.$toast({
          //   message: '录音失败，请重试',
          //   type: 'fail'
          // });
          this.stopVoiceInput();
        };
        
        // 开始录音
        this.recorder.start();
        // 注意：不在这里显示 Toast，等到 OnRecognitionStart 时再显示
      
        
        // 初始化语音识别器
        this.speechRecognizer = new SpeechRecognizer(this.speechConfig);
        
        // 设置语音识别回调
        this.setupSpeechRecognitionCallbacks();
        
        // 建立语音识别连接
        this.speechRecognizer.start();
        
      } catch (error) {
        console.error('[VoiceInput] 启动语音输入失败:', error);
        
        this.isInitializing = false; // 重置初始化状态
        
        // 上报：启动失败
        this.reportToAegis({
          type: 'voice_input_start_error',
          error: error?.message || JSON.stringify(error),
          ext1: 'voice-input-error',
          trace: 'voice-input-start-error',
        });
        
        this.$toast({
          message: '启动语音输入失败',
          type: 'fail'
        });
        this.stopVoiceInput();
      }
    },
    
    // 停止语音输入
    stopVoiceInput() {
      const wasRecording = this.isRecording;
      this.isRecording = false;
      this.isInitializing = false; // 重置初始化状态
      
      // 清除自动停止计时器
      this.clearAutoStopTimer();
      
      // 停止录音
      if (this.recorder) {
        try {
          this.recorder.stop();
        } catch (e) {
          console.error('[VoiceInput] 停止录音失败:', e);
        }
        this.recorder = null;
      }
      
      // 停止语音识别
      if (this.speechRecognizer) {
        try {
          if (this.isCanStop) {
            this.speechRecognizer.stop();
          }
        } catch (e) {
          console.error('[VoiceInput] 停止语音识别失败:', e);
        }
        this.speechRecognizer = null;
      }
      
      this.isCanSendData = false;
      this.isCanStop = false;
      
      // 上报：停止语音输入
      if (wasRecording && this.sessionId) {
        const duration = this.startTime ? Date.now() - this.startTime : 0;
        this.reportToAegis({
          type: 'voice_input_stop',
          action: 'stop',
          duration: duration,
          hasText: !!this.recognitionText,
          textLength: this.recognitionText?.length || 0,
          ext1: 'voice-input-action',
          trace: 'voice-input-stop',
        });
      }
    },
    
    // 重置自动停止计时器
    resetAutoStopTimer() {
      this.clearAutoStopTimer();
      
      // 根据是否有输入内容决定延迟时间
      const hasInput = this.recognitionText && this.recognitionText.trim().length > 0;
      const delay = hasInput ? this.AUTO_STOP_DELAY_HAS_INPUT : this.AUTO_STOP_DELAY_EMPTY;
      const delayText = hasInput ? '1.5秒' : '3秒';
      
      this.autoStopTimer = setTimeout(() => {
        console.log(`[VoiceInput] ${delayText}无输入，自动停止录音`);
        
        // 上报：自动停止
        this.reportToAegis({
          type: 'voice_input_auto_stop',
          reason: `${delayText}无输入`,
          hasInput: hasInput,
          recognitionText: this.recognitionText || '',
          textLength: this.recognitionText?.length || 0,
          ext1: 'voice-input-action',
          trace: 'voice-input-auto-stop',
        });
        
        if (this.isRecording) {
          this.stopVoiceInput();

        }
      }, delay);
    },
    
    // 清除自动停止计时器
    clearAutoStopTimer() {
      if (this.autoStopTimer) {
        clearTimeout(this.autoStopTimer);
        this.autoStopTimer = null;
      }
    },
    
    // 设置语音识别回调
    setupSpeechRecognitionCallbacks() {
      if (!this.speechRecognizer) return;
      
      // 开始识别
      this.speechRecognizer.OnRecognitionStart = (res) => {
        console.log('[VoiceInput] 开始识别', res);
        
        // 重置初始化状态，显示录音中图标
        this.isInitializing = false;
        
        this.isCanSendData = true;
        this.isCanStop = true;
        
        // 显示提示信息
        this.$toast({
          message: '请开始语音输入',
        });
        
        // 上报：语音识别开始
        this.reportToAegis({
          type: 'voice_input_recognition_start',
          action: 'recognition_start',
          ext1: 'voice-input-recognition',
          trace: 'voice-input-recognition-start',
        });
        
        // 开始识别时启动自动停止计时器
        this.resetAutoStopTimer();
      };
      
      // 识别变化时 - 实时更新输入框
      this.speechRecognizer.OnRecognitionResultChange = (res) => {
        if (res?.voice_text_str) {
          const currentText = `${this.resultText}${res.voice_text_str}`;
          this.recognitionText = this.replaceRecognitionText(currentText);
          // 实时更新输入框的值
          this.updateInputValue(this.recognitionText);
          // 有新输入时重置计时器
          this.resetAutoStopTimer();
          
          // 上报：实时识别文本变化（采样上报，避免过于频繁）
          if (Math.random() < 0.1) { // 10% 采样率
            this.reportToAegis({
              type: 'voice_input_recognition_change',
              action: 'recognition_change',
              currentTextLength: this.recognitionText?.length || 0,
              ext1: 'voice-input-recognition',
              trace: 'voice-input-recognition-change',
            });
          }
        }
      };
      
      // 一句话结束
      this.speechRecognizer.OnSentenceEnd = (res) => {
        console.log('[VoiceInput] 一句话结束', res);
        if (res?.voice_text_str) {
          this.resultText += res.voice_text_str;
          this.recognitionText = this.replaceRecognitionText(this.resultText);
          this.updateInputValue(this.recognitionText);
          // 一句话结束时重置计时器
          this.resetAutoStopTimer();
          
          // 上报：一句话结束
          this.reportToAegis({
            type: 'voice_input_sentence_end',
            action: 'sentence_end',
            sentenceText: res.voice_text_str || '',
            totalTextLength: this.recognitionText?.length || 0,
            ext1: 'voice-input-recognition',
            trace: 'voice-input-sentence-end',
          });
        }
      };
      
      // 识别结束
      this.speechRecognizer.OnRecognitionComplete = (res) => {
        console.log('[VoiceInput] 识别结束', res);
        this.isCanSendData = false;
        this.isCanStop = false;
        
        const hasText = !!this.recognitionText;
        const duration = this.startTime ? Date.now() - this.startTime : 0;
        
        // 上报：语音识别完成（包含完整的用户输入）
        this.reportToAegis({
          type: 'voice_input_recognition_complete',
          action: 'recognition_complete',
          hasText: hasText,
          textLength: this.recognitionText?.length || 0,
          recognitionText: this.recognitionText || '',
          duration: duration,
          ext1: 'voice-input-recognition',
          trace: hasText ? 'voice-input-recognition-success' : 'voice-input-recognition-empty',
        });
        
        if (this.recognitionText) {
          this.updateInputValue(this.recognitionText);
        }
        this.$emit('stopVoiceInput', this.recognitionText);
        
        this.stopVoiceInput();
      };
      
      // 识别错误
      this.speechRecognizer.OnError = (res) => {
        console.error('[VoiceInput] 语音识别失败:', res);
        
        const duration = this.startTime ? Date.now() - this.startTime : 0;
        
        // 上报：语音识别错误
        this.reportToAegis({
          type: 'voice_input_recognition_error',
          action: 'recognition_error',
          error: res?.msg || res?.message || JSON.stringify(res),
          duration: duration,
          partialText: this.recognitionText || '',
          ext1: 'voice-input-error',
          trace: 'voice-input-recognition-error',
        });
        
        // this.$toast({
        //   message: '语音识别失败，请重试',
        //   type: 'fail'
        // });
        this.stopVoiceInput();
      };
    },
    
    // 更新输入框的值
    updateInputValue(text) {
      this.$emit('input', text);
      this.$emit('change', text);
    },
    
    // 替换识别文本中的特殊字符
    replaceRecognitionText(text) {
      if (!text) return '';
      return text
    },
    
    // 清理资源
    cleanup() {
      this.stopVoiceInput();
    }
  }
};
</script>

<style scoped>
.voice-input-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  color: #1989fa;
  transition: all 0.3s ease;
  position: relative;
  flex-shrink: 0; /* 防止被压缩 */
  margin-left: 1px; /* 缩小与相邻图标的间距 */
  vertical-align: middle;
}

.voice-input-icon:hover {
  color: #66b1ff;
}

.voice-input-icon:active {
  transform: scale(0.95);
}

.voice-input-icon.recording {
  color: #fff; /* 白色图标 */
  background-color: #1989fa; /* 蓝色背景 */
  border-radius: 50%;
}

.voice-input-icon.loading {
  color: #1989fa;
  cursor: not-allowed;
}

.loading-animation {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.recording-animation {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 24px;
  height: 24px;
  border: 2px solid currentColor;
  border-radius: 50%;
  animation: pulse 1.5s ease-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}
</style>
